/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Renew16 } from '@carbon/icons-react';
import {
  Button,
  ContentSwitcher,
  FormLabel,
  Link,
  Switch,
  Tooltip
} from 'carbon-components-react';
import cx from 'classnames';
import urlJoin from 'proper-url-join';
import { useIntl, FormattedMessage } from 'react-intl';

import { useAppSelector } from '../../../../store';
import { messages as ConfigOptionMessages } from '../../../discovery/components/search-detail-config/messages';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../../discovery/util/constants';
import { selectIsInsightActionsEnabled } from '../../store/features/selectors';
import {
  getAssistantId,
  selectInstanceId
} from '../../store/session/selectors';
import { conversationalSearchGALinks } from './links';
import { RadioSliderSelector } from './RadioSliderSelector';

export interface SearchConfigurationProps {
  draft: {
    isConversationalSearchEnabled: boolean;
    setConversationalSearchRetrievalConfidenceThreshold: (string) => void;
    conversationalSearchRetrievalConfidenceThreshold: string;
    setConversationalSearchConfidenceThreshold: (string) => void;
    conversationalSearchConfidenceThreshold: string;
    setConversationalSearchVerbosityOption: (string) => void;
    conversationalSearchVerbosityOption: string;
    setConversationalSearchResponseConfidenceThreshold: (string) => void;
    conversationalSearchResponseConfidenceThreshold: string;
  };
  readOnly: boolean;
}

export const SearchConfiguration = ({
  draft,
  readOnly
}: SearchConfigurationProps) => {
  const { formatMessage: t } = useIntl();
  const isInsightActionsEnabled = useAppSelector(selectIsInsightActionsEnabled);
  const instanceId = useAppSelector(selectInstanceId);
  const assistantId = useAppSelector(getAssistantId);

  const onRetrievalConfidenceThresholdChange = name => {
    draft.setConversationalSearchRetrievalConfidenceThreshold(name);
  };

  const onConfidenceThresholdChange = name => {
    draft.setConversationalSearchConfidenceThreshold(name);
  };

  const onVerbosityThresholdChange = name => {
    draft.setConversationalSearchVerbosityOption(name);
  };

  const onResponseConfidenceThresholdChange = name => {
    draft.setConversationalSearchResponseConfidenceThreshold(name);
  };

  const getRetreivalConfidenceThresholdIndex = () => {
    const value = draft.conversationalSearchRetrievalConfidenceThreshold;
    switch (value) {
      case CONFIDENCE_THRESHOLD_VALUE.RARELY:
        return 0;
      case CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN:
        return 1;
      case CONFIDENCE_THRESHOLD_VALUE.MORE_OFTEN:
        return 2;
      case CONFIDENCE_THRESHOLD_VALUE.MOST_OFTEN:
        return 3;
      default:
        return 1;
    }
  };

  const getConfidenceThresholdIndex = () => {
    const value = draft.conversationalSearchConfidenceThreshold;
    switch (value) {
      case CONFIDENCE_THRESHOLD_VALUE.RARELY:
        return 0;
      case CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN:
        return 1;
      case CONFIDENCE_THRESHOLD_VALUE.MORE_OFTEN:
        return 2;
      case CONFIDENCE_THRESHOLD_VALUE.MOST_OFTEN:
        return 3;
    }
  };

  const getVerbosityThresholdIndex = () => {
    const value = draft.conversationalSearchVerbosityOption;
    switch (value) {
      case RESPONSE_LENGTH_VALUE.CONCISE:
        return 0;
      case RESPONSE_LENGTH_VALUE.MODERATE:
        return 1;
      case RESPONSE_LENGTH_VALUE.VERBOSE:
        return 2;
    }
  };

  const getResponseConfidenceThresholdIndex = () => {
    const value = draft.conversationalSearchResponseConfidenceThreshold;
    switch (value) {
      case CONFIDENCE_THRESHOLD_VALUE.RARELY:
        return 0;
      case CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN:
        return 1;
      case CONFIDENCE_THRESHOLD_VALUE.MORE_OFTEN:
        return 2;
      case CONFIDENCE_THRESHOLD_VALUE.MOST_OFTEN:
        return 3;
      default:
        return 1;
    }
  };

  const restoreDefault = () => {
    onRetrievalConfidenceThresholdChange(CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN);
    onVerbosityThresholdChange(RESPONSE_LENGTH_VALUE.MODERATE);
    onResponseConfidenceThresholdChange(CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN);
  };

  const convoSearchEnabledReadonly =
    !draft.isConversationalSearchEnabled || readOnly;

  const labelText = (titleText: object, tooltipText: object, link?: string) => {
    return (
      <>
        <FormLabel
          className={cx('bx--label', {
            'bx--label--disabled': convoSearchEnabledReadonly
          })}></FormLabel>
        <Tooltip align="start" triggerText={t(titleText)} tabIndex={0}>
          <>
            <FormattedMessage {...tooltipText} />
            {link && (
              <Link href={link} target="_blank" rel="noopener noreferrer">
                <FormattedMessage {...ConfigOptionMessages.learnMore} />
              </Link>
            )}
          </>
        </Tooltip>
      </>
    );
  };

  return (
    <>
      {isInsightActionsEnabled && (
        <>
          <div className="ConversationalSearch__title-container">
            <div
              className={cx('ConversationalSearch__title', {
                'ConfigureContent__multiturnDescription--disabled':
                  convoSearchEnabledReadonly
              })}>
              <h5>{t(ConfigOptionMessages.title)}</h5>
            </div>
            <div className="ConversationalSearch__restoreDefaultButton">
              <Button
                kind="ghost"
                size="sm"
                onClick={restoreDefault}
                disabled={convoSearchEnabledReadonly}>
                {t(ConfigOptionMessages.restoreDefault)}
                {<Renew16 />}
              </Button>
            </div>
          </div>
          <div
            className={cx('ConversationalSearch__description', {
              'ConfigureContent__multiturnDescription--disabled':
                convoSearchEnabledReadonly
            })}>
            <FormattedMessage {...ConfigOptionMessages.description} />
            <Link
              onClick={() =>
                window.location.replace(
                  urlJoin(
                    document.baseURI,
                    encodeURIComponent(instanceId),
                    'assistants',
                    assistantId,
                    'insight-actions'
                  )
                )
              }>
              {t(ConfigOptionMessages.descriptionLink)}
            </Link>
          </div>
          <div className="ConfigureContent__conversationalSearchConfiguration-container">
            <div className="ConfigureContent__conversationalSearch-RadioSliderSelector-container">
              <RadioSliderSelector
                readOnly={convoSearchEnabledReadonly}
                labelText={labelText(
                  ConfigOptionMessages.conversationalSearchRetrievalConfidenceTitle,
                  ConfigOptionMessages.conversationalSearchRetrievalConfidenceTooltip
                )}
                radioSliderName={'retrieval-confidence-threshold-radio-slider'}
                labels={[
                  t(ConfigOptionMessages.lowest),
                  t(ConfigOptionMessages.low),
                  t(ConfigOptionMessages.high),
                  t(ConfigOptionMessages.highest)
                ]}
                selectedIndex={getRetreivalConfidenceThresholdIndex()}
                selectedValue={
                  draft.conversationalSearchRetrievalConfidenceThreshold
                }
                onSelectionChange={
                  onRetrievalConfidenceThresholdChange
                }></RadioSliderSelector>
            </div>
            <div className="ConfigureContent__conversationalSearch-RadioSliderSelector-container">
              <RadioSliderSelector
                readOnly={convoSearchEnabledReadonly}
                labelText={labelText(
                  ConfigOptionMessages.conversationalSearchResponseTitle,
                  ConfigOptionMessages.conversationalSearchResponseLengthTooltip,
                  conversationalSearchGALinks.verbosity
                )}
                radioSliderName={'verbosity-radio-slider'}
                labels={[
                  t(ConfigOptionMessages.conversationalSearchResponseConcise),
                  t(ConfigOptionMessages.conversationalSearchResponseModerate),
                  t(ConfigOptionMessages.conversationalSearchResponseVerbose)
                ]}
                selectedIndex={getVerbosityThresholdIndex()}
                selectedValue={draft.conversationalSearchVerbosityOption}
                onSelectionChange={
                  onVerbosityThresholdChange
                }></RadioSliderSelector>
            </div>
            <div className="ConfigureContent__conversationalSearch-RadioSliderSelector-container">
              <RadioSliderSelector
                readOnly={convoSearchEnabledReadonly}
                labelText={labelText(
                  ConfigOptionMessages.conversationalSearchResponseConfidenceTitle,
                  ConfigOptionMessages.conversationalSearchResponseConfidenceTooltip
                )}
                radioSliderName={'response-confidence-threshold-radio-slider'}
                labels={[
                  t(ConfigOptionMessages.lowest),
                  t(ConfigOptionMessages.low),
                  t(ConfigOptionMessages.high),
                  t(ConfigOptionMessages.highest)
                ]}
                selectedIndex={getResponseConfidenceThresholdIndex()}
                selectedValue={
                  draft.conversationalSearchResponseConfidenceThreshold
                }
                onSelectionChange={
                  onResponseConfidenceThresholdChange
                }></RadioSliderSelector>
            </div>
          </div>
        </>
      )}
      {!isInsightActionsEnabled && (
        <>
          <div className="ConfigureContent__conversationalSearchConfidenceThreshold-container">
            <FormLabel
              className={cx('bx--label', {
                'bx--label--disabled': convoSearchEnabledReadonly
              })}>
              <FormattedMessage
                {...ConfigOptionMessages.conversationalSearchConfidenceTitle}
              />
            </FormLabel>
            <ContentSwitcher
              onChange={({ name }) => onConfidenceThresholdChange(name)}
              size="md"
              selectedIndex={getConfidenceThresholdIndex()}>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={CONFIDENCE_THRESHOLD_VALUE.RARELY}
                text={t(
                  ConfigOptionMessages.conversationalSearchConfidenceRarely
                )}></Switch>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN}
                text={t(
                  ConfigOptionMessages.conversationalSearchConfidenceLessOften
                )}></Switch>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={CONFIDENCE_THRESHOLD_VALUE.MORE_OFTEN}
                text={t(
                  ConfigOptionMessages.conversationalSearchConfidenceMoreOften
                )}></Switch>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={CONFIDENCE_THRESHOLD_VALUE.MOST_OFTEN}
                text={t(
                  ConfigOptionMessages.conversationalSearchConfidenceMostOften
                )}></Switch>
            </ContentSwitcher>
          </div>
          <div className="ConfigureContent__conversationalSearchConfidenceThreshold-container">
            <FormLabel
              className={cx('bx--label', {
                'bx--label--disabled': convoSearchEnabledReadonly
              })}>
              <Tooltip
                align="start"
                triggerText={t(
                  ConfigOptionMessages.conversationalSearchResponseTitle
                )}
                tabIndex={0}>
                <>
                  <FormattedMessage
                    {...ConfigOptionMessages.conversationalSearchResponseLengthTooltip}
                  />
                  <Link
                    href={conversationalSearchGALinks.verbosity}
                    target="_blank"
                    rel="noopener noreferrer">
                    <FormattedMessage {...ConfigOptionMessages.learnMore} />
                  </Link>
                </>
              </Tooltip>
            </FormLabel>
            <ContentSwitcher
              onChange={({ name }) => onVerbosityThresholdChange(name)}
              size="md"
              selectedIndex={getVerbosityThresholdIndex()}>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={RESPONSE_LENGTH_VALUE.CONCISE}
                text={t(
                  ConfigOptionMessages.conversationalSearchResponseConcise
                )}></Switch>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={RESPONSE_LENGTH_VALUE.MODERATE}
                text={t(
                  ConfigOptionMessages.conversationalSearchResponseModerate
                )}></Switch>
              <Switch
                disabled={convoSearchEnabledReadonly}
                name={RESPONSE_LENGTH_VALUE.VERBOSE}
                text={t(
                  ConfigOptionMessages.conversationalSearchResponseVerbose
                )}></Switch>
            </ContentSwitcher>
          </div>
        </>
      )}
    </>
  );
};
